import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { GsapClassNames } from '@components/Sections/constants';

import { getMediaqueryDual, uid } from '@utils/utils';

gsap.registerPlugin(ScrollTrigger);

const matchMedia = gsap.matchMedia();

export const animateSectionBase = (
  currentSection: HTMLDivElement | null,
  textBlockY: number,
  conditions?: gsap.Conditions,
  isLongText?: boolean,
  isFinalSection?: boolean,
) => {
  const { isLg } = conditions as gsap.Conditions;

  gsap.fromTo(
    `.${GsapClassNames.TitlePart}`,
    {
      opacity: 0,
      y: 150,
    },
    {
      opacity: 1,
      y: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: currentSection,
        start: 'top 90%',
        end: 'top top',
        scrub: true,
      },
    },
  );

  gsap.fromTo(
    `.${GsapClassNames.TextBlock}`,
    {
      opacity: 0,
      y: textBlockY,
    },
    {
      opacity: 1,
      y: () => {
        if (isFinalSection) return 0;
        if (isLg) {
          if (isLongText) return 190;
          return 75;
        }
        return 95;
      },
      ease: isLg ? 'power2.out' : 'none',
      scrollTrigger: {
        trigger: `.${GsapClassNames.TextBlock}`,
        start: 'top 85%',
        endTrigger: isLg || isFinalSection ? `.${GsapClassNames.TextBlock}` : `.${GsapClassNames.Quote}`,
        end: () => {
          if (isFinalSection) return '+=100';
          if (isLg) return 'top 30px';
          return 'top 50px';
        },
        scrub: !isFinalSection,
      },
    },
  );

  if (!isFinalSection) {
    gsap.fromTo(
      `.${GsapClassNames.Quote}`,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: currentSection,
          start: 'top 40%',
          end: 'top top',
          scrub: true,
        },
      },
    );

    gsap.fromTo(
      `.${GsapClassNames.Dependent}`,
      { opacity: 1 },
      {
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: `.${GsapClassNames.TextBlock}`,
          start: 'top 95%',
          end: '+=150',
          scrub: true,
        },
      },
    );

    gsap.fromTo(
      `.${GsapClassNames.Independent}`,
      { opacity: 1 },
      {
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: `.${GsapClassNames.Quote}`,
          start: () => `bottom ${window.innerHeight - 50}`,
          end: isLg && isFinalSection ? '+=100' : 'top 80px',
          scrub: true,
        },
      },
    );

    ScrollTrigger.create({
      trigger: `.${GsapClassNames.Quote}`,
      start: isLg ? 'top 25px' : 'top 40px',
      end: () => `+=${window.innerHeight}`,
      toggleClass: GsapClassNames.Pinned,
    });
  }
};

interface AnimationProps {
  animationTarget: SVGPathElement | HTMLElement | string | null;
  animationTrigger?: SVGSVGElement | SVGPathElement | HTMLElement | string | null;
  animationDelay?: number;
  animationStartPositions?: string | number | ScrollTrigger.StartEndFunc;
  animationEndPositions?: string | number | ScrollTrigger.StartEndFunc;
}

interface AnimationSVGProps extends AnimationProps {
  pathLength: number;
}

export const animateSVGPath = ({
  animationTarget,
  pathLength,
  animationTrigger = animationTarget,
  animationDelay,
  animationStartPositions = () => `bottom ${window.innerHeight - 40}`,
  animationEndPositions,
}: AnimationSVGProps) => {
  if (!animationTarget) {
    return;
  }

  matchMedia.add(getMediaqueryDual('desktopAnimation'), () => {
    const scrollTriggerId = `path0${uid()}`;

    const timeline = gsap
      .timeline({
        scrollTrigger: {
          id: scrollTriggerId,
          trigger: animationTrigger,
          start: animationStartPositions,
          end: animationEndPositions,
          toggleActions: 'play complete none reverse',
          invalidateOnRefresh: true,
        },
      })
      .fromTo(
        animationTarget,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.1,
          delay: animationDelay,
        },
      )
      .fromTo(
        animationTarget,
        {
          strokeDashoffset: pathLength + 2,
        },
        {
          strokeDashoffset: 0,
          duration: 0.8,
        },
      );

    return () => {
      timeline.kill();
      ScrollTrigger.getById(scrollTriggerId)?.kill();
      gsap.set(animationTarget, { clearProps: true });
    };
  });
};

interface IllustationPathsProps {
  illustration: string;
  trigger?: HTMLElement | string | null;
  startPosition?: string;
  endPosition?: string;
  delay?: number;
}

export const setAnimationToIllustrationsPaths = ({
  illustration,
  trigger,
  startPosition,
  endPosition,
  delay,
}: IllustationPathsProps) => {
  gsap.utils.toArray(illustration).forEach((path) => {
    const pathEl = path as SVGPathElement;
    const pathElLength = pathEl.getTotalLength();

    pathEl.setAttribute('stroke-dasharray', `${pathElLength}`);
    animateSVGPath({
      animationTarget: pathEl,
      pathLength: pathElLength,
      animationTrigger: trigger || illustration,
      animationDelay: delay,
      animationStartPositions: startPosition,
      animationEndPositions: endPosition,
    });
  });
};
